import type { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";

type Stop = Pick<
  AdminGetRoutesLocationsResDto,
  | "locationId"
  | "order"
  | "travelDetails"
  | "serviceTime"
  | "driverStatus"
  | "driverServiceStartTime"
  | "driverServiceEndTime"
>;

const isServiceDuration = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const fallbackServiceMinutes = (stop: Stop): number => {
  if (
    (stop.driverStatus === "completed" ||
      stop.driverStatus === "partiallyCompleted" ||
      stop.driverStatus === "cantPlow") &&
    stop.driverServiceStartTime &&
    stop.driverServiceEndTime
  ) {
    const recordedMinutes =
      (Date.parse(stop.driverServiceEndTime) -
        Date.parse(stop.driverServiceStartTime)) /
      60000;
    if (isServiceDuration(recordedMinutes)) return recordedMinutes;
  }

  return isServiceDuration(stop.serviceTime) ? stop.serviceTime : 0;
};

/**
 * The server leaves skipped stops out of every estimate and lists them last,
 * so their stored times are leftovers from before they were skipped.
 */
export const isSkippedStop = (
  stop: Pick<AdminGetRoutesLocationsResDto, "order">,
): boolean => stop.order?.status === "skipped";

/**
 * Each stop's own service minutes from the cumulative serviceTimeMin the
 * server stores, read in list order (the order its estimates walk the route).
 * A skipped stop takes 0 and is nobody's previous stop. Missing, nonfinite or
 * decreasing totals fall back to valid recorded service time, then configured
 * service time, then zero. The next stop reestablishes the cumulative baseline
 * so an invalid total cannot inflate its duration either.
 */
export const stopServiceMinutes = (stops: Stop[]): Record<string, number> => {
  const minutes: Record<string, number> = {};
  let isFirst = true;
  let previousTotal: number | undefined;

  stops.forEach((stop) => {
    if (isSkippedStop(stop)) {
      minutes[stop.locationId] = 0;
      return;
    }

    const total = stop.travelDetails?.serviceTimeMin;
    const duration = isServiceDuration(total)
      ? isFirst
        ? total
        : previousTotal !== undefined
          ? total - previousTotal
          : undefined
      : undefined;
    minutes[stop.locationId] = isServiceDuration(duration)
      ? duration
      : fallbackServiceMinutes(stop);

    previousTotal =
      isServiceDuration(total) &&
      (duration === undefined || isServiceDuration(duration))
        ? total
        : undefined;
    isFirst = false;
  });

  return minutes;
};
